import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  View, Text, TextInput, Modal, StyleSheet,
  TouchableOpacity, ActivityIndicator, Switch,
  ScrollView, useWindowDimensions, Pressable, Animated, Easing, Image,
} from 'react-native';
import {
  fetchUsers, createUser, updateUser, deleteUser, bulkAddWallet, UserModel,
} from '../query/userService';
import { notifyUser, bulkNotify, personalize } from '../query/notificationService';

const C = {
  primary: '#29A05A', primaryLight: 'rgba(41,160,90,0.15)', primaryDark: '#047E33',
  success: '#10b981', successLight: 'rgba(16,185,129,0.15)',
  danger: '#ef4444', dangerLight: 'rgba(239,68,68,0.15)',
  warning: '#f59e0b', warningLight: 'rgba(245,158,11,0.15)',
  text: '#E2EDE6', muted: '#7A9A80', border: 'rgba(255,255,255,0.09)', bg: '#0E1512',
  rowAlt: '#1A2820',
};

type FilterType = 'all' | 'active' | 'inactive';
type Tri = 'any' | 'yes' | 'no';
type SortKey = 'name' | 'phone' | 'wallet' | 'orders' | 'status' | 'created' | 'seen';
type SortDir = 'asc' | 'desc';
type ToastType = 'success' | 'error';

const TABLE_MIN_WIDTH = 1160;

// Column flex weights (shared by header + rows so they stay aligned)
const COLS = {
  sn: 0.5,
  user: 2.4,
  phone: 1.5,
  wallet: 1.1,
  orders: 0.9,
  status: 1.2,
  created: 1.3,
  seen: 1.3,
  actions: 1.9,
};

// Field defined outside to prevent TextInput focus loss on re-render
const F = ({ label, value, onChange, keyType, multiline, readonly, hint }: any) => (
  <View style={ms.fg}>
    <Text style={ms.label}>{label}</Text>
    <TextInput
      style={[ms.input, multiline && { height: 80, textAlignVertical: 'top' }, readonly && { backgroundColor: '#141E18', color: C.muted }]}
      value={value} onChangeText={readonly ? undefined : onChange}
      placeholder={label} keyboardType={keyType || 'default'}
      multiline={multiline} placeholderTextColor={C.muted} editable={!readonly}
    />
    {hint ? <Text style={ms.hint}>{hint}</Text> : null}
  </View>
);

const fmtDate = (ts: any): string => {
  try {
    const d = ts?.toDate ? ts.toDate() : (ts instanceof Date ? ts : null);
    if (!d) return '—';
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return '—'; }
};

const tsMillis = (ts: any): number => {
  try { return ts?.toMillis ? ts.toMillis() : (ts?.seconds ? ts.seconds * 1000 : 0); }
  catch { return 0; }
};

// Defensive timestamp parser: handles Firestore Timestamp | ISO string | epoch (s or ms)
const anyToMillis = (v: any): number => {
  if (v == null) return 0;
  if (typeof v === 'object') {
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.seconds === 'number') return v.seconds * 1000;
  }
  if (typeof v === 'string') { const t = Date.parse(v); return isNaN(t) ? 0 : t; }
  if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
  return 0;
};

// Mirrors the admin app's fallback chain for "last seen"
const lastSeenMs = (u: UserModel): number =>
  anyToMillis(u.lastOpened ?? u.lastOpenedAt ?? (u as any).lastLogin ?? u.lastActive ?? (u as any).loginTime);

const fmtRelative = (ms: number): string => {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return 'just now';
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

const initialOf = (u: UserModel) => (u.username || u.phone || 'U').charAt(0).toUpperCase();

// Avatar: real profile pic when available, otherwise a coloured initial
const Avatar = ({ u, size = 38 }: { u: UserModel; size?: number }) => {
  if (u.profilePicUrl) {
    return <Image source={{ uri: u.profilePicUrl }} style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: '#e2e8f0' }} />;
  }
  return (
    <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: u.notactive ? '#94a3b8' : C.primary, justifyContent: 'center', alignItems: 'center' }}>
      <Text style={{ color: '#fff', fontSize: size * 0.42, fontWeight: '700' }}>{initialOf(u)}</Text>
    </View>
  );
};

// Tri-state segmented control (Any / Yes / No) used by reachability filters
const Seg = ({ label, value, onChange }: { label: string; value: Tri; onChange: (v: Tri) => void }) => (
  <View style={st.advField}>
    <Text style={st.advLabel}>{label}</Text>
    <View style={{ flexDirection: 'row', gap: 6 }}>
      {(['any', 'yes', 'no'] as Tri[]).map(o => (
        <TouchableOpacity key={o} style={[st.segBtn, value === o && st.segBtnActive]} onPress={() => onChange(o)}>
          <Text style={[st.segTxt, value === o && st.segTxtActive]}>{o === 'any' ? 'Any' : o === 'yes' ? 'Yes' : 'No'}</Text>
        </TouchableOpacity>
      ))}
    </View>
  </View>
);

const PAGE_SIZE = 10;

const TBIcon = ({ name }: { name: string }) => (
  <span className="material-icons-round" style={{ fontSize: 18, color: '#4ECDC4', lineHeight: 1 }}>{name}</span>
);

export default function UserManagementScreen() {
  const { width } = useWindowDimensions();
  const isMobile = width < 720;

  const [users, setUsers] = useState<UserModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterType>('all');
  const [sortKey, setSortKey] = useState<SortKey>('created');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);

  // Advanced filters
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fMinOrders, setFMinOrders] = useState('');
  const [fMaxOrders, setFMaxOrders] = useState('');
  const [fMinWallet, setFMinWallet] = useState('');
  const [fMaxWallet, setFMaxWallet] = useState('');
  const [fInactiveDays, setFInactiveDays] = useState('');     // NOT seen for ≥ N days (dormant)
  const [fSeenWithinDays, setFSeenWithinDays] = useState(''); // seen within last N days (recently active)
  const [fJoinedWithinDays, setFJoinedWithinDays] = useState(''); // new signups
  const [fJoinedBeforeDays, setFJoinedBeforeDays] = useState(''); // older than N days
  const [fHasToken, setFHasToken] = useState<Tri>('any');     // reachable by push
  const [fNotif, setFNotif] = useState<Tri>('any');           // notificationsEnabled
  const [fWhatsapp, setFWhatsapp] = useState<Tri>('any');     // whatsappEnabled

  // Bulk actions
  const [walletOpen, setWalletOpen] = useState(false);
  const [walletAmount, setWalletAmount] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<string>('');

  // Modal / form
  const [modalVisible, setModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<UserModel | null>(null); // null => create mode
  const [deleteTarget, setDeleteTarget] = useState<UserModel | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Notify modal
  const [notifyTarget, setNotifyTarget] = useState<UserModel | null>(null);
  const [notifyTitle, setNotifyTitle] = useState('');
  const [notifyBody, setNotifyBody] = useState('');
  const [notifyImage, setNotifyImage] = useState('');
  const [sending, setSending] = useState(false);
  const [notifyError, setNotifyError] = useState('');
  const [notifyBulk, setNotifyBulk] = useState(false); // true => send to all filtered users

  const [phone, setPhone] = useState('');
  const [username, setUsername] = useState('');
  const [walletBalance, setWalletBalance] = useState('0');
  const [fcmToken, setFcmToken] = useState('');
  const [notactive, setNotactive] = useState(false);
  const [formError, setFormError] = useState('');

  // Right-drawer slide animation for Add/Edit
  const drawerW = Math.min(isMobile ? width : 480, width);
  const slideX = useRef(new Animated.Value(drawerW)).current;
  useEffect(() => {
    if (modalVisible) {
      slideX.setValue(drawerW);
      Animated.timing(slideX, { toValue: 0, duration: 260, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
    }
  }, [modalVisible, drawerW, slideX]);
  const closeModal = () => {
    Animated.timing(slideX, { toValue: drawerW, duration: 200, easing: Easing.in(Easing.cubic), useNativeDriver: true })
      .start(() => setModalVisible(false));
  };

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: ToastType } | null>(null);
  const toastTimer = useRef<any>(null);
  const showToast = (msg: string, type: ToastType = 'success') => {
    setToast({ msg, type });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2800);
  };

  useEffect(() => { load(); return () => { if (toastTimer.current) clearTimeout(toastTimer.current); }; }, []);

  const load = async () => {
    setLoading(true);
    try { setUsers(await fetchUsers()); }
    catch { showToast('Failed to load users', 'error'); }
    finally { setLoading(false); }
  };

  // ── Derived: filter + search + sort ──
  const filtered = useMemo(() => {
    let list = [...users];
    if (filter === 'active') list = list.filter(u => !u.notactive);
    else if (filter === 'inactive') list = list.filter(u => u.notactive);

    // Advanced filters
    const DAY = 86400000;
    const now = Date.now();
    const minO = parseInt(fMinOrders, 10);
    const maxO = parseInt(fMaxOrders, 10);
    const minW = parseFloat(fMinWallet);
    const maxW = parseFloat(fMaxWallet);
    const inactD = parseInt(fInactiveDays, 10);
    const seenD = parseInt(fSeenWithinDays, 10);
    const joinedInD = parseInt(fJoinedWithinDays, 10);
    const joinedBeforeD = parseInt(fJoinedBeforeDays, 10);

    if (!isNaN(minO)) list = list.filter(u => (u.orderCount || 0) >= minO);
    if (!isNaN(maxO)) list = list.filter(u => (u.orderCount || 0) <= maxO);
    if (!isNaN(minW)) list = list.filter(u => (u.walletBalance || 0) >= minW);
    if (!isNaN(maxW)) list = list.filter(u => (u.walletBalance || 0) <= maxW);
    if (!isNaN(inactD) && inactD > 0) {
      const cutoff = now - inactD * DAY; // not seen since cutoff (or never)
      list = list.filter(u => { const ms = lastSeenMs(u); return ms === 0 || ms < cutoff; });
    }
    if (!isNaN(seenD) && seenD > 0) {
      const cutoff = now - seenD * DAY; // seen within window
      list = list.filter(u => { const ms = lastSeenMs(u); return ms > 0 && ms >= cutoff; });
    }
    if (!isNaN(joinedInD) && joinedInD > 0) {
      const cutoff = now - joinedInD * DAY; // joined within window
      list = list.filter(u => { const ms = tsMillis(u.createdTimestamp); return ms > 0 && ms >= cutoff; });
    }
    if (!isNaN(joinedBeforeD) && joinedBeforeD > 0) {
      const cutoff = now - joinedBeforeD * DAY; // joined more than N days ago
      list = list.filter(u => { const ms = tsMillis(u.createdTimestamp); return ms > 0 && ms < cutoff; });
    }
    if (fHasToken !== 'any') list = list.filter(u => fHasToken === 'yes' ? !!u.fcmToken : !u.fcmToken);
    if (fNotif !== 'any') list = list.filter(u => fNotif === 'yes' ? u.notificationsEnabled !== false : u.notificationsEnabled === false);
    if (fWhatsapp !== 'any') list = list.filter(u => fWhatsapp === 'yes' ? u.whatsappEnabled !== false : u.whatsappEnabled === false);

    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(u =>
        (u.phone || '').toLowerCase().includes(q) ||
        (u.username || '').toLowerCase().includes(q) ||
        (u.userId || '').toLowerCase().includes(q)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      switch (sortKey) {
        case 'name': return (a.username || '').localeCompare(b.username || '') * dir;
        case 'phone': return (a.phone || '').localeCompare(b.phone || '') * dir;
        case 'wallet': return ((a.walletBalance || 0) - (b.walletBalance || 0)) * dir;
        case 'orders': return ((a.orderCount || 0) - (b.orderCount || 0)) * dir;
        case 'status': return ((a.notactive ? 1 : 0) - (b.notactive ? 1 : 0)) * dir;
        case 'created': return (tsMillis(a.createdTimestamp) - tsMillis(b.createdTimestamp)) * dir;
        case 'seen': return (lastSeenMs(a) - lastSeenMs(b)) * dir;
        default: return 0;
      }
    });
    return list;
  }, [users, search, filter, sortKey, sortDir, fMinOrders, fMaxOrders, fMinWallet, fMaxWallet, fInactiveDays, fSeenWithinDays, fJoinedWithinDays, fJoinedBeforeDays, fHasToken, fNotif, fWhatsapp]);

  // Reset to first page whenever the result set changes
  useEffect(() => { setPage(1); }, [search, filter, sortKey, sortDir, fMinOrders, fMaxOrders, fMinWallet, fMaxWallet, fInactiveDays, fSeenWithinDays, fJoinedWithinDays, fJoinedBeforeDays, fHasToken, fNotif, fWhatsapp]);

  const advancedActive = !!(fMinOrders || fMaxOrders || fMinWallet || fMaxWallet || fInactiveDays || fSeenWithinDays || fJoinedWithinDays || fJoinedBeforeDays || fHasToken !== 'any' || fNotif !== 'any' || fWhatsapp !== 'any');
  const clearAdvanced = () => {
    setFMinOrders(''); setFMaxOrders(''); setFMinWallet(''); setFMaxWallet('');
    setFInactiveDays(''); setFSeenWithinDays(''); setFJoinedWithinDays(''); setFJoinedBeforeDays('');
    setFHasToken('any'); setFNotif('any'); setFWhatsapp('any');
  };

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const activeCount = users.filter(u => !u.notactive).length;
  const inactiveCount = users.filter(u => u.notactive).length;
  const totalWallet = users.reduce((s, u) => s + (u.walletBalance || 0), 0);
  const totalOrders = users.reduce((s, u) => s + (u.orderCount || 0), 0);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir(key === 'created' || key === 'wallet' ? 'desc' : 'asc'); }
  };

  // ── CRUD handlers ──
  const openCreate = () => {
    setEditingUser(null);
    setPhone(''); setUsername(''); setWalletBalance('0'); setFcmToken(''); setNotactive(false);
    setFormError('');
    setModalVisible(true);
  };

  const openEdit = (user: UserModel) => {
    setEditingUser(user);
    setPhone(user.phone ?? '');
    setUsername(user.username ?? '');
    setWalletBalance(String(user.walletBalance ?? 0));
    setFcmToken(user.fcmToken ?? '');
    setNotactive(user.notactive ?? false);
    setFormError('');
    setModalVisible(true);
  };

  const handleSave = async () => {
    const p = phone.trim();
    if (!p) { setFormError('Phone number is required.'); return; }
    if (p.replace(/[^0-9]/g, '').length < 6) { setFormError('Enter a valid phone number.'); return; }
    const wallet = parseFloat(walletBalance);
    if (isNaN(wallet) || wallet < 0) { setFormError('Wallet balance must be a positive number.'); return; }

    setSaving(true);
    setFormError('');
    try {
      if (editingUser) {
        await updateUser(editingUser.userId, {
          phone: p,
          username: username.trim() || null,
          walletBalance: wallet,
          fcmToken: fcmToken.trim(),
          notactive,
        });
        setUsers(prev => prev.map(u =>
          u.userId === editingUser.userId
            ? { ...u, phone: p, username: username.trim() || null, walletBalance: wallet, fcmToken: fcmToken.trim(), notactive }
            : u
        ));
        showToast('User updated successfully');
      } else {
        await createUser({
          userId: '',
          phone: p,
          username: username.trim() || null,
          walletBalance: wallet,
          fcmToken: fcmToken.trim(),
          notactive,
        });
        await load(); // reload so the new Firestore doc id is picked up
        showToast('User created successfully');
      }
      closeModal();
    } catch (e: any) {
      setFormError(`Save failed: ${e?.message ?? 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteUser(deleteTarget.userId);
      setUsers(prev => prev.filter(u => u.userId !== deleteTarget.userId));
      showToast('User deleted');
      setDeleteTarget(null);
    } catch {
      showToast('Failed to delete user', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (user: UserModel) => {
    const newVal = !user.notactive;
    // optimistic
    setUsers(prev => prev.map(u => u.userId === user.userId ? { ...u, notactive: newVal } : u));
    try { await updateUser(user.userId, { notactive: newVal }); }
    catch {
      setUsers(prev => prev.map(u => u.userId === user.userId ? { ...u, notactive: !newVal } : u));
      showToast('Failed to update status', 'error');
    }
  };

  const resetNotifyForm = () => {
    setNotifyTitle(''); setNotifyBody(''); setNotifyImage(''); setNotifyError('');
  };
  const openNotify = (user: UserModel) => { setNotifyBulk(false); setNotifyTarget(user); resetNotifyForm(); };
  const openBulkNotify = () => { setNotifyTarget(null); setNotifyBulk(true); resetNotifyForm(); };
  const closeNotify = () => { setNotifyTarget(null); setNotifyBulk(false); };

  const notifyRecipients = filtered.filter(u => !!u.fcmToken); // those reachable via push

  const handleSendNotify = async () => {
    if (!notifyTitle.trim()) { setNotifyError('Title is required.'); return; }
    if (!notifyBody.trim()) { setNotifyError('Message body is required.'); return; }
    setSending(true);
    setNotifyError('');
    try {
      const common = {
        title: notifyTitle.trim(),
        body: notifyBody.trim(),
        imageUrl: notifyImage.trim() || undefined,
        screen: 'home',
      };
      if (notifyBulk) {
        setBulkProgress(`Sending 0 / ${notifyRecipients.length}…`);
        const res = await bulkNotify(
          notifyRecipients.map(u => ({ fcmToken: u.fcmToken, name: u.username })),
          common, // {name} in title/body is personalized per recipient
          (done, total, sent) => setBulkProgress(`Sent ${sent} / ${total}…`),
        );
        setBulkProgress('');
        showToast(`Sent ${res.sent} · failed ${res.failed} · skipped ${res.skipped} (no token)`, res.failed ? 'error' : 'success');
        closeNotify();
      } else if (notifyTarget) {
        await notifyUser(notifyTarget.userId, {
          fcmToken: notifyTarget.fcmToken,
          ...common,
          title: personalize(common.title, notifyTarget.username),
          body: personalize(common.body, notifyTarget.username),
        });
        showToast(`Notification sent to ${notifyTarget.username || notifyTarget.phone}`);
        closeNotify();
      }
    } catch (e: any) {
      setBulkProgress('');
      setNotifyError(e?.message ?? 'Failed to send notification.');
    } finally {
      setSending(false);
    }
  };

  const handleBulkWallet = async () => {
    const amount = parseFloat(walletAmount);
    if (isNaN(amount) || amount === 0) { showToast('Enter a non-zero amount', 'error'); return; }
    setBulkBusy(true);
    try {
      const ids = filtered.map(u => u.userId);
      await bulkAddWallet(ids, amount, (done, total) => setBulkProgress(`Updating ${done} / ${total}…`));
      setBulkProgress('');
      // reflect locally without a full reload
      setUsers(prev => prev.map(u =>
        ids.includes(u.userId) ? { ...u, walletBalance: (u.walletBalance || 0) + amount } : u,
      ));
      showToast(`${amount > 0 ? 'Added' : 'Deducted'} ₹${Math.abs(amount)} for ${ids.length} users`);
      setWalletOpen(false);
      setWalletAmount('');
    } catch (e: any) {
      setBulkProgress('');
      showToast(`Wallet update failed: ${e?.message ?? 'error'}`, 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  // ── Table header cell ──
  const Th = ({ label, k, flex, align }: { label: string; k?: SortKey; flex: number; align?: 'left' | 'center' | 'right' }) => (
    <Pressable
      style={[tb.th, { flex }, align === 'center' && { alignItems: 'center' }, align === 'right' && { alignItems: 'flex-end' }]}
      onPress={k ? () => toggleSort(k) : undefined}
      disabled={!k}
    >
      <View style={tb.thInner}>
        <Text style={tb.thTxt}>{label}</Text>
        {k && sortKey === k ? <Text style={tb.thArrow}>{sortDir === 'asc' ? '▲' : '▼'}</Text> : null}
      </View>
    </Pressable>
  );

  const Row = ({ item, index }: { item: UserModel; index: number }) => (
    <View style={[tb.row, index % 2 === 1 && { backgroundColor: C.rowAlt }]}>
      {/* # */}
      <View style={[tb.td, { flex: COLS.sn, alignItems: 'center' }]}>
        <Text style={tb.snTxt}>{(safePage - 1) * PAGE_SIZE + index + 1}</Text>
      </View>
      {/* User */}
      <View style={[tb.td, { flex: COLS.user, flexDirection: 'row', alignItems: 'center', gap: 10 }]}>
        <Avatar u={item} size={38} />
        <View style={{ flex: 1 }}>
          <Text style={tb.name} numberOfLines={1}>{item.username || 'No name'}</Text>
          <Text style={tb.idTxt} numberOfLines={1}>ID: {item.userId}</Text>
        </View>
      </View>
      {/* Phone */}
      <View style={[tb.td, { flex: COLS.phone }]}>
        <Text style={tb.cellTxt} numberOfLines={1}>{item.phone || '—'}</Text>
      </View>
      {/* Wallet */}
      <View style={[tb.td, { flex: COLS.wallet }]}>
        {item.walletBalance > 0 ? (
          <View style={tb.walletBadge}><Text style={tb.walletTxt}>₹{item.walletBalance}</Text></View>
        ) : <Text style={[tb.cellTxt, { color: C.muted }]}>₹0</Text>}
      </View>
      {/* Orders */}
      <View style={[tb.td, { flex: COLS.orders, alignItems: 'center' }]}>
        <View style={[tb.orderBadge, !(item.orderCount) && tb.orderBadgeZero]}>
          <Text style={[tb.orderTxt, !(item.orderCount) && tb.orderTxtZero]}>{item.orderCount || 0}</Text>
        </View>
      </View>
      {/* Status */}
      <View style={[tb.td, { flex: COLS.status }]}>
        <TouchableOpacity
          style={[tb.statusPill, { backgroundColor: item.notactive ? C.dangerLight : C.successLight }]}
          onPress={() => toggleActive(item)}
        >
          <View style={[tb.statusDot, { backgroundColor: item.notactive ? C.danger : C.success }]} />
          <Text style={[tb.statusTxt, { color: item.notactive ? C.danger : C.success }]}>
            {item.notactive ? 'Inactive' : 'Active'}
          </Text>
        </TouchableOpacity>
      </View>
      {/* Created */}
      <View style={[tb.td, { flex: COLS.created }]}>
        <Text style={tb.cellTxt}>{fmtDate(item.createdTimestamp)}</Text>
      </View>
      {/* Last Seen */}
      <View style={[tb.td, { flex: COLS.seen }]}>
        {(() => {
          const ms = lastSeenMs(item);
          const online = ms > 0 && Date.now() - ms < 5 * 60 * 1000;
          return (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              {online && <View style={tb.onlineDot} />}
              <Text style={[tb.cellTxt, !ms && { color: C.muted }]}>{fmtRelative(ms)}</Text>
            </View>
          );
        })()}
      </View>
      {/* Actions */}
      <View style={[tb.td, { flex: COLS.actions, flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }]}>
        <TouchableOpacity style={tb.notifyBtn} onPress={() => openNotify(item)}>
          <Text style={tb.notifyTxt}>🔔</Text>
        </TouchableOpacity>
        <TouchableOpacity style={tb.editBtn} onPress={() => openEdit(item)}>
          <Text style={tb.editTxt}>Edit</Text>
        </TouchableOpacity>
        <TouchableOpacity style={tb.delBtn} onPress={() => setDeleteTarget(item)}>
          <Text style={tb.delTxt}>Delete</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  return (
    <View style={st.container}>
      {/* Toolbar */}
      <View style={[st.controls, isMobile && { flexWrap: 'wrap', alignItems: 'stretch', gap: 10 }]}>
        <View style={st.tbTitle}>
          <TBIcon name="group" />
          <View>
            <Text style={st.tbTitleText}>Users</Text>
            <Text style={st.tbTitleSub}>Customer accounts & wallet balances</Text>
          </View>
        </View>
        <View style={st.ctrlBtns}>
          <TouchableOpacity style={st.addBtn} onPress={openCreate}>
            <Text style={st.addTxt}>＋ Add User</Text>
          </TouchableOpacity>
          <TouchableOpacity style={st.refreshBtn} onPress={load} disabled={loading}>
            <Text style={st.refreshTxt}>⟳ Refresh</Text>
          </TouchableOpacity>
        </View>
        <View style={st.searchBox}>
          <Text style={{ fontSize: 14, marginRight: 8 }}>🔍</Text>
          <TextInput
            style={st.searchInput} placeholder="Search by phone, name or ID…"
            value={search} onChangeText={setSearch} placeholderTextColor="#8BAAB8"
          />
          {search ? <TouchableOpacity onPress={() => setSearch('')}><Text style={{ color: '#8BAAB8' }}>✕</Text></TouchableOpacity> : null}
        </View>
        <View style={st.filterRow}>
          {(['all', 'active', 'inactive'] as FilterType[]).map(f => (
            <TouchableOpacity key={f} style={[st.filterBtn, filter === f && st.filterBtnActive]} onPress={() => setFilter(f)}>
              <Text style={[st.filterTxt, filter === f && st.filterTxtActive]}>
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </Text>
            </TouchableOpacity>
          ))}
          <TouchableOpacity
            style={[st.filterBtn, (showAdvanced || advancedActive) && st.filterBtnActive]}
            onPress={() => setShowAdvanced(s => !s)}
          >
            <Text style={[st.filterTxt, (showAdvanced || advancedActive) && st.filterTxtActive]}>
              ⚙ Advanced{advancedActive ? ' ●' : ''}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Advanced filter panel */}
      {showAdvanced && (
        <View style={st.advPanel}>
          <View style={st.advRow}>
            <View style={st.advField}>
              <Text style={st.advLabel}>Order count</Text>
              <View style={st.advInline}>
                <TextInput style={st.advInput} value={fMinOrders} onChangeText={setFMinOrders} placeholder="min" placeholderTextColor={C.muted} keyboardType="number-pad" />
                <Text style={st.advDash}>–</Text>
                <TextInput style={st.advInput} value={fMaxOrders} onChangeText={setFMaxOrders} placeholder="max" placeholderTextColor={C.muted} keyboardType="number-pad" />
              </View>
            </View>
            <View style={st.advField}>
              <Text style={st.advLabel}>Wallet ₹</Text>
              <View style={st.advInline}>
                <TextInput style={st.advInput} value={fMinWallet} onChangeText={setFMinWallet} placeholder="min" placeholderTextColor={C.muted} keyboardType="decimal-pad" />
                <Text style={st.advDash}>–</Text>
                <TextInput style={st.advInput} value={fMaxWallet} onChangeText={setFMaxWallet} placeholder="max" placeholderTextColor={C.muted} keyboardType="decimal-pad" />
              </View>
            </View>
            <View style={st.advField}>
              <Text style={st.advLabel}>Not active ≥ days</Text>
              <TextInput style={[st.advInput, { width: 96 }]} value={fInactiveDays} onChangeText={setFInactiveDays} placeholder="e.g. 30" placeholderTextColor={C.muted} keyboardType="number-pad" />
            </View>
            <View style={st.advField}>
              <Text style={st.advLabel}>Active within days</Text>
              <TextInput style={[st.advInput, { width: 96 }]} value={fSeenWithinDays} onChangeText={setFSeenWithinDays} placeholder="e.g. 7" placeholderTextColor={C.muted} keyboardType="number-pad" />
            </View>
            <View style={st.advField}>
              <Text style={st.advLabel}>Joined within days</Text>
              <TextInput style={[st.advInput, { width: 96 }]} value={fJoinedWithinDays} onChangeText={setFJoinedWithinDays} placeholder="e.g. 7" placeholderTextColor={C.muted} keyboardType="number-pad" />
            </View>
            <View style={st.advField}>
              <Text style={st.advLabel}>Joined ≥ days ago</Text>
              <TextInput style={[st.advInput, { width: 96 }]} value={fJoinedBeforeDays} onChangeText={setFJoinedBeforeDays} placeholder="e.g. 14" placeholderTextColor={C.muted} keyboardType="number-pad" />
            </View>
            <Seg label="Push reachable" value={fHasToken} onChange={setFHasToken} />
            <Seg label="Notifications on" value={fNotif} onChange={setFNotif} />
            <Seg label="WhatsApp on" value={fWhatsapp} onChange={setFWhatsapp} />
          </View>

          <View style={st.advPresets}>
            <Text style={st.advHint}>Re-engage:</Text>
            <TouchableOpacity style={st.presetBtn} onPress={() => { clearAdvanced(); setFMinOrders(''); setFMaxOrders('0'); }}><Text style={st.presetTxt}>Never ordered</Text></TouchableOpacity>
            <TouchableOpacity style={st.presetBtn} onPress={() => { clearAdvanced(); setFMinOrders('1'); setFMaxOrders('1'); }}><Text style={st.presetTxt}>One-time buyers</Text></TouchableOpacity>
            <TouchableOpacity style={st.presetBtn} onPress={() => { clearAdvanced(); setFMinOrders('5'); }}><Text style={st.presetTxt}>Loyal (5+)</Text></TouchableOpacity>
            <TouchableOpacity style={st.presetBtn} onPress={() => { clearAdvanced(); setFInactiveDays('30'); setFMinOrders('1'); }}><Text style={st.presetTxt}>Lapsed (30d, ordered before)</Text></TouchableOpacity>
            <TouchableOpacity style={st.presetBtn} onPress={() => { clearAdvanced(); setFJoinedBeforeDays('14'); setFMaxOrders('0'); }}><Text style={st.presetTxt}>Dormant signups</Text></TouchableOpacity>
            <TouchableOpacity style={st.presetBtn} onPress={() => { clearAdvanced(); setFJoinedWithinDays('7'); }}><Text style={st.presetTxt}>New (7d)</Text></TouchableOpacity>
            <TouchableOpacity style={st.presetBtn} onPress={() => { clearAdvanced(); setFMinWallet('1'); }}><Text style={st.presetTxt}>Has wallet ₹</Text></TouchableOpacity>
            <TouchableOpacity style={st.presetBtn} onPress={() => { clearAdvanced(); setFHasToken('yes'); }}><Text style={st.presetTxt}>Push-reachable</Text></TouchableOpacity>
            {advancedActive && <TouchableOpacity style={st.presetClear} onPress={clearAdvanced}><Text style={st.presetClearTxt}>Clear all</Text></TouchableOpacity>}
          </View>
        </View>
      )}

      {/* Matched-set bulk actions bar */}
      {!loading && advancedActive && filtered.length > 0 && (
        <View style={[st.bulkBar, isMobile && { flexDirection: 'column', alignItems: 'stretch', gap: 10 }]}>
          <Text style={st.bulkInfo}>
            <Text style={{ fontWeight: '800', color: C.primary }}>{filtered.length}</Text> users match
            {'  ·  '}{notifyRecipients.length} reachable by push
          </Text>
          <View style={st.bulkBtns}>
            <TouchableOpacity style={st.bulkNotifyBtn} onPress={openBulkNotify} disabled={bulkBusy}>
              <Text style={st.bulkNotifyTxt}>🔔 Notify all</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.bulkWalletBtn} onPress={() => { setWalletAmount(''); setWalletOpen(true); }} disabled={bulkBusy}>
              <Text style={st.bulkWalletTxt}>💰 Add wallet</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Table */}
      {loading ? (
        <View style={st.loader}><ActivityIndicator size="large" color={C.primary} /></View>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator style={{ flex: 1 }} contentContainerStyle={{ minWidth: TABLE_MIN_WIDTH, flexGrow: 1 }}>
          <View style={tb.table}>
            {/* Header */}
            <View style={tb.headerRow}>
              <Th label="#" flex={COLS.sn} align="center" />
              <Th label="User" k="name" flex={COLS.user} />
              <Th label="Phone" k="phone" flex={COLS.phone} />
              <Th label="Wallet" k="wallet" flex={COLS.wallet} />
              <Th label="Orders" k="orders" flex={COLS.orders} align="center" />
              <Th label="Status" k="status" flex={COLS.status} />
              <Th label="Joined" k="created" flex={COLS.created} />
              <Th label="Last Seen" k="seen" flex={COLS.seen} />
              <Th label="Actions" flex={COLS.actions} align="right" />
            </View>
            {/* Body */}
            <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 30 }}>
              {filtered.length === 0 ? (
                <View style={st.empty}>
                  <Text style={{ fontSize: 48 }}>👤</Text>
                  <Text style={st.emptyTitle}>No users found</Text>
                  <Text style={st.emptySub}>Try adjusting your search or filters.</Text>
                </View>
              ) : (
                paged.map((item, index) => <Row key={item.userId} item={item} index={index} />)
              )}
            </ScrollView>
          </View>
        </ScrollView>
      )}

      {/* Bottom bar: counts + pagination combined */}
      {!loading && (
        <View style={[st.bottomBar, isMobile && { flexDirection: 'column', gap: 12 }]}>
          <View style={[st.statGroup, isMobile && { flexWrap: 'wrap', justifyContent: 'center' }]}>
            {[
              { label: 'Total Users', val: users.length.toLocaleString(), color: C.primary },
              { label: 'Active', val: activeCount.toLocaleString(), color: C.success },
              { label: 'Inactive', val: inactiveCount.toLocaleString(), color: C.danger },
              { label: 'Total Orders', val: totalOrders.toLocaleString(), color: C.primaryDark },
              { label: 'Wallet Total', val: `₹${totalWallet.toLocaleString()}`, color: C.warning },
            ].map(({ label, val, color }, i) => (
              <React.Fragment key={label}>
                {i > 0 && <View style={st.statSep} />}
                <View style={st.statChip}>
                  <Text style={[st.statChipVal, { color }]}>{val}</Text>
                  <Text style={st.statChipLbl}>{label}</Text>
                </View>
              </React.Fragment>
            ))}
          </View>

          {filtered.length > 0 && (
            <View style={st.pageBtns}>
              <Text style={st.footerCount}>
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, filtered.length)} of {filtered.length}
              </Text>
              <TouchableOpacity
                style={[st.pageBtn, safePage <= 1 && st.pageBtnDisabled]}
                onPress={() => setPage(p => Math.max(1, p - 1))} disabled={safePage <= 1}
              >
                <Text style={[st.pageBtnTxt, safePage <= 1 && st.pageBtnTxtDisabled]}>‹ Prev</Text>
              </TouchableOpacity>
              <Text style={st.pageInfo}>Page {safePage} / {totalPages}</Text>
              <TouchableOpacity
                style={[st.pageBtn, safePage >= totalPages && st.pageBtnDisabled]}
                onPress={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}
              >
                <Text style={[st.pageBtnTxt, safePage >= totalPages && st.pageBtnTxtDisabled]}>Next ›</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      )}

      {/* ── Add / Edit Modal ── */}
      <Modal visible={modalVisible} animationType="fade" transparent onRequestClose={closeModal}>
        <View style={ms.root}>
          <Pressable style={ms.backdrop} onPress={closeModal} />
          <Animated.View style={[ms.drawer, { width: drawerW, transform: [{ translateX: slideX }] }]}>
          <View style={ms.header}>
            <TouchableOpacity style={ms.closeBtn} onPress={closeModal}>
              <Text style={ms.closeTxt}>✕</Text>
            </TouchableOpacity>
            <Text style={ms.title}>{editingUser ? 'Edit User' : 'Add New User'}</Text>
            <TouchableOpacity style={[ms.saveBtn, saving && { opacity: 0.6 }]} onPress={handleSave} disabled={saving}>
              {saving ? <ActivityIndicator color="#fff" size="small" /> : <Text style={ms.saveTxt}>{editingUser ? 'Save' : 'Create'}</Text>}
            </TouchableOpacity>
          </View>

          {/* Summary chip (edit only) */}
          {editingUser && (
            <View style={ms.userChip}>
              <Avatar u={editingUser} size={42} />
              <View style={{ flex: 1 }}>
                <Text style={ms.chipName}>{editingUser.username || 'No name'}</Text>
                <Text style={ms.chipId}>ID: {editingUser.userId}</Text>
              </View>
              <View style={ms.chipOrders}>
                <Text style={ms.chipOrdersVal}>{editingUser.orderCount || 0}</Text>
                <Text style={ms.chipOrdersLbl}>orders</Text>
              </View>
            </View>
          )}

          <ScrollView style={{ flex: 1 }} contentContainerStyle={ms.scroll} keyboardShouldPersistTaps="handled">
            {formError ? (
              <View style={ms.errBox}><Text style={ms.errTxt}>⚠ {formError}</Text></View>
            ) : null}

            <Text style={ms.section}>Contact Info</Text>
            <F label="Phone *" value={phone} onChange={setPhone} keyType="phone-pad" />
            <F label="Display Name" value={username} onChange={setUsername} />

            <Text style={ms.section}>Wallet</Text>
            <F label="Wallet Balance (₹)" value={walletBalance} onChange={setWalletBalance} keyType="decimal-pad" />

            <Text style={ms.section}>Device</Text>
            <F label="FCM Token" value={fcmToken} onChange={setFcmToken} multiline hint="Push-notification device token (optional)." />

            <Text style={ms.section}>Account Status</Text>
            <View style={ms.switchRow}>
              <View>
                <Text style={ms.switchLabel}>Account Active</Text>
                <Text style={ms.switchSub}>Allow user to access the app</Text>
              </View>
              <Switch
                value={!notactive}
                onValueChange={v => setNotactive(!v)}
                trackColor={{ false: C.border, true: C.primary }}
                thumbColor="#fff"
              />
            </View>

            <View style={{ height: 20 }} />
          </ScrollView>
          </Animated.View>
        </View>
      </Modal>

      {/* ── Delete confirm modal (works on web, unlike Alert.alert) ── */}
      <Modal visible={!!deleteTarget} animationType="fade" transparent>
        <Pressable style={cf.overlay} onPress={() => !deleting && setDeleteTarget(null)}>
          <Pressable style={cf.card} onPress={() => {}}>
            <View style={cf.iconWrap}><Text style={{ fontSize: 26 }}>🗑️</Text></View>
            <Text style={cf.title}>Delete user?</Text>
            <Text style={cf.body}>
              {deleteTarget ? `This will permanently delete ${deleteTarget.username || deleteTarget.phone}. This cannot be undone.` : ''}
            </Text>
            <View style={cf.actions}>
              <TouchableOpacity style={cf.cancelBtn} onPress={() => setDeleteTarget(null)} disabled={deleting}>
                <Text style={cf.cancelTxt}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[cf.confirmBtn, deleting && { opacity: 0.6 }]} onPress={confirmDelete} disabled={deleting}>
                {deleting ? <ActivityIndicator color="#fff" size="small" /> : <Text style={cf.confirmTxt}>Delete</Text>}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* ── Notify modal (single + bulk) ── */}
      <Modal visible={!!notifyTarget || notifyBulk} animationType="fade" transparent>
        <Pressable style={cf.overlay} onPress={() => !sending && closeNotify()}>
          <Pressable style={[cf.card, { maxWidth: 440, alignItems: 'stretch' }]} onPress={() => {}}>
            <View style={nf.header}>
              <Text style={{ fontSize: 22 }}>🔔</Text>
              <View style={{ flex: 1 }}>
                <Text style={nf.title}>{notifyBulk ? 'Notify Filtered Users' : 'Send Notification'}</Text>
                {notifyBulk
                  ? <Text style={nf.sub}>{notifyRecipients.length} of {filtered.length} reachable by push</Text>
                  : notifyTarget ? <Text style={nf.sub}>To {notifyTarget.username || notifyTarget.phone}</Text> : null}
              </View>
            </View>

            {notifyBulk && notifyRecipients.length === 0 && (
              <View style={nf.warn}><Text style={nf.warnTxt}>⚠ None of the {filtered.length} matched users have an FCM token — nothing to send.</Text></View>
            )}
            {!notifyBulk && notifyTarget && !notifyTarget.fcmToken && (
              <View style={nf.warn}><Text style={nf.warnTxt}>⚠ This user has no FCM token saved — sending will fail.</Text></View>
            )}
            {notifyError ? <View style={ms.errBox}><Text style={ms.errTxt}>⚠ {notifyError}</Text></View> : null}

            <View style={nf.labelRow}>
              <Text style={ms.label}>Title *</Text>
              <View style={nf.tokenBtns}>
                <TouchableOpacity style={nf.tokenBtn} onPress={() => setNotifyTitle('Hi Mr {name}')}>
                  <Text style={nf.tokenTxt}>Hi Mr {'{name}'}</Text>
                </TouchableOpacity>
                <TouchableOpacity style={nf.tokenBtn} onPress={() => setNotifyTitle(t => t + '{name}')}>
                  <Text style={nf.tokenTxt}>+ {'{name}'}</Text>
                </TouchableOpacity>
              </View>
            </View>
            <TextInput style={ms.input} value={notifyTitle} onChangeText={setNotifyTitle} placeholder="e.g. Hi Mr {name}" placeholderTextColor={C.muted} />
            <Text style={nf.hint}>Tip: type <Text style={{ fontWeight: '700', color: C.primary }}>{'{name}'}</Text> and it's replaced with each user's name.</Text>
            {(notifyTitle.includes('{name}') || notifyBody.includes('{name}')) && (
              <Text style={nf.hint}>Preview: “{personalize(notifyTitle || '(no title)', notifyBulk ? (notifyRecipients[0]?.username) : notifyTarget?.username)}”</Text>
            )}
            <View style={{ height: 12 }} />
            <Text style={ms.label}>Message *</Text>
            <TextInput style={[ms.input, { height: 80, textAlignVertical: 'top' }]} value={notifyBody} onChangeText={setNotifyBody} placeholder="Message body" placeholderTextColor={C.muted} multiline />
            <View style={{ height: 12 }} />
            <Text style={ms.label}>Image URL (optional)</Text>
            <TextInput style={ms.input} value={notifyImage} onChangeText={setNotifyImage} placeholder="https://…" placeholderTextColor={C.muted} />
            {notifyImage.trim() ? <Image source={{ uri: notifyImage.trim() }} style={nf.preview} /> : null}

            {sending && notifyBulk && bulkProgress ? <Text style={nf.progress}>{bulkProgress}</Text> : null}

            <View style={[cf.actions, { marginTop: 18 }]}>
              <TouchableOpacity style={cf.cancelBtn} onPress={closeNotify} disabled={sending}>
                <Text style={cf.cancelTxt}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[nf.sendBtn, (sending || (notifyBulk && notifyRecipients.length === 0)) && { opacity: 0.6 }]}
                onPress={handleSendNotify}
                disabled={sending || (notifyBulk && notifyRecipients.length === 0)}
              >
                {sending ? <ActivityIndicator color="#fff" size="small" /> : <Text style={nf.sendTxt}>{notifyBulk ? `Send to ${notifyRecipients.length}` : 'Send'}</Text>}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* ── Bulk wallet modal ── */}
      <Modal visible={walletOpen} animationType="fade" transparent>
        <Pressable style={cf.overlay} onPress={() => !bulkBusy && setWalletOpen(false)}>
          <Pressable style={[cf.card, { maxWidth: 400, alignItems: 'stretch' }]} onPress={() => {}}>
            <View style={nf.header}>
              <Text style={{ fontSize: 22 }}>💰</Text>
              <View style={{ flex: 1 }}>
                <Text style={nf.title}>Add Wallet Balance</Text>
                <Text style={nf.sub}>Applies to all {filtered.length} matched users</Text>
              </View>
            </View>
            <View style={nf.warn}>
              <Text style={nf.warnTxt}>⚠ This credits every matched user's wallet and cannot be undone in bulk. Use a negative amount to deduct.</Text>
            </View>
            <Text style={ms.label}>Amount (₹)</Text>
            <TextInput style={ms.input} value={walletAmount} onChangeText={setWalletAmount} placeholder="e.g. 50  (or -50 to deduct)" placeholderTextColor={C.muted} keyboardType="numbers-and-punctuation" />
            {bulkBusy && bulkProgress ? <Text style={nf.progress}>{bulkProgress}</Text> : null}
            <View style={[cf.actions, { marginTop: 18 }]}>
              <TouchableOpacity style={cf.cancelBtn} onPress={() => setWalletOpen(false)} disabled={bulkBusy}>
                <Text style={cf.cancelTxt}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[nf.sendBtn, bulkBusy && { opacity: 0.6 }]} onPress={handleBulkWallet} disabled={bulkBusy}>
                {bulkBusy ? <ActivityIndicator color="#fff" size="small" /> : <Text style={nf.sendTxt}>Apply to {filtered.length}</Text>}
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* ── Toast ── */}
      {toast && (
        <View style={[st.toast, { backgroundColor: toast.type === 'error' ? C.danger : C.success }]}>
          <Text style={st.toastTxt}>{toast.type === 'error' ? '✕ ' : '✓ '}{toast.msg}</Text>
        </View>
      )}
    </View>
  );
}

const st = StyleSheet.create({
  container: { flex: 1, backgroundColor: C.bg, padding: 16 },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  headerM: { flexDirection: 'column', alignItems: 'flex-start', gap: 12 },
  title: { fontSize: 24, fontWeight: '700', color: C.text },
  sub: { fontSize: 12, color: C.muted, marginTop: 2 },
  headerActions: { flexDirection: 'row', gap: 10, alignItems: 'center' },
  refreshBtn: { backgroundColor: 'rgba(33,128,141,0.15)', borderWidth: 1, borderColor: 'rgba(33,128,141,0.3)', paddingHorizontal: 12, paddingVertical: 7, borderRadius: 8 },
  refreshTxt: { color: '#4ECDC4', fontWeight: '600', fontSize: 12 },
  addBtn: { backgroundColor: '#4ECDC4', paddingHorizontal: 14, paddingVertical: 7, borderRadius: 8 },
  addTxt: { color: '#0A1712', fontWeight: '700', fontSize: 12 },

  bottomBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#17201B', borderRadius: 12, borderWidth: 1, borderColor: C.border, paddingVertical: 9, paddingHorizontal: 14, marginTop: 8, flexWrap: 'wrap', gap: 10 },
  statGroup: { flexDirection: 'row', alignItems: 'center' },
  statChip: { flexDirection: 'row', alignItems: 'baseline', gap: 6, paddingHorizontal: 12, paddingVertical: 2 },
  statChipVal: { fontSize: 16, fontWeight: '800' },
  statChipLbl: { fontSize: 11, color: C.muted, fontWeight: '600' },
  statSep: { width: 1, height: 16, backgroundColor: C.border },

  controls: { flexDirection: 'row', alignItems: 'center', gap: 10, backgroundColor: '#0A1712', paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: 'rgba(33,128,141,0.18)', flexWrap: 'wrap' },
  tbTitle: { flexDirection: 'row', alignItems: 'center', gap: 8, marginRight: 'auto' as any },
  tbTitleText: { fontSize: 15, fontWeight: '800', color: '#fff' },
  tbTitleSub: { fontSize: 11, color: '#8BAAB8', marginTop: 1 },
  ctrlBtns: { flexDirection: 'row', gap: 8 },
  searchBox: { flex: 1, flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 8, borderWidth: 1, borderColor: 'rgba(33,128,141,0.25)', paddingHorizontal: 10, height: 36, minWidth: 160 },
  searchInput: { flex: 1, fontSize: 13, color: '#e2f0f2', outlineStyle: 'none' as any },
  filterRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap' },
  filterBtn: { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 20, backgroundColor: 'transparent', borderWidth: 1, borderColor: 'rgba(33,128,141,0.3)' },
  filterBtnActive: { backgroundColor: 'rgba(78,205,196,0.15)', borderColor: '#4ECDC4' },
  filterTxt: { fontSize: 12, fontWeight: '600', color: '#8BAAB8' },
  filterTxtActive: { color: '#4ECDC4' },

  advPanel: { backgroundColor: '#17201B', borderRadius: 12, borderWidth: 1, borderColor: C.border, padding: 14, marginBottom: 14 },
  advRow: { flexDirection: 'row', gap: 20, flexWrap: 'wrap', alignItems: 'flex-end' },
  advField: { gap: 6 },
  advLabel: { fontSize: 11, fontWeight: '700', color: C.muted, textTransform: 'uppercase' as const, letterSpacing: 0.5 },
  advInline: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  advInput: { width: 76, borderWidth: 1, borderColor: C.border, borderRadius: 8, paddingHorizontal: 10, paddingVertical: 8, fontSize: 13, color: C.text, backgroundColor: '#fafafa', outlineStyle: 'none' as any },
  advDash: { color: C.muted, fontWeight: '700' },
  segBtn: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8, backgroundColor: '#141E18', borderWidth: 1, borderColor: C.border },
  segBtnActive: { backgroundColor: C.primary, borderColor: C.primary },
  segTxt: { fontSize: 12, fontWeight: '700', color: C.muted },
  segTxtActive: { color: '#fff' },
  advPresets: { flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  advHint: { fontSize: 12, color: C.muted, fontWeight: '600' },
  presetBtn: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: C.primaryLight },
  presetTxt: { fontSize: 12, fontWeight: '600', color: C.primary },
  presetClear: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: C.dangerLight },
  presetClearTxt: { fontSize: 12, fontWeight: '600', color: C.danger },

  bulkBar: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#eef6f8', borderRadius: 12, borderWidth: 1, borderColor: C.primaryLight, paddingVertical: 10, paddingHorizontal: 14, marginBottom: 14 },
  bulkInfo: { fontSize: 13, color: C.text },
  bulkBtns: { flexDirection: 'row', gap: 10 },
  bulkNotifyBtn: { backgroundColor: C.primary, borderRadius: 9, paddingHorizontal: 16, paddingVertical: 9 },
  bulkNotifyTxt: { color: '#fff', fontWeight: '700', fontSize: 13 },
  bulkWalletBtn: { backgroundColor: C.warning, borderRadius: 9, paddingHorizontal: 16, paddingVertical: 9 },
  bulkWalletTxt: { color: '#fff', fontWeight: '700', fontSize: 13 },

  loader: { flex: 1, justifyContent: 'center', alignItems: 'center', paddingTop: 60 },
  empty: { alignItems: 'center', paddingTop: 60 },
  emptyTitle: { fontSize: 18, fontWeight: '600', color: C.text, marginTop: 12 },
  emptySub: { fontSize: 13, color: C.muted, marginTop: 4 },
  footerCount: { fontSize: 12, color: C.muted, fontWeight: '600', marginRight: 4 },
  pageBtns: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  pageBtn: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 9, borderWidth: 1, borderColor: C.border, backgroundColor: '#17201B' },
  pageBtnDisabled: { backgroundColor: '#141E18', borderColor: 'rgba(255,255,255,0.06)' },
  pageBtnTxt: { fontSize: 13, fontWeight: '700', color: C.primary },
  pageBtnTxtDisabled: { color: '#cbd5e1' },
  pageInfo: { fontSize: 13, fontWeight: '600', color: C.text, minWidth: 96, textAlign: 'center' },

  toast: { position: 'absolute', bottom: 24, alignSelf: 'center', paddingHorizontal: 20, paddingVertical: 12, borderRadius: 10, shadowColor: '#000', shadowOffset: { width: 0, height: 3 }, shadowOpacity: 0.2, shadowRadius: 8, elevation: 6 },
  toastTxt: { color: '#fff', fontWeight: '600', fontSize: 13 },
});

const tb = StyleSheet.create({
  table: { flex: 1, backgroundColor: '#17201B', borderRadius: 14, overflow: 'hidden', borderWidth: 1, borderColor: C.border },
  headerRow: { flexDirection: 'row', backgroundColor: '#0A1712', borderBottomWidth: 1, borderBottomColor: C.border, paddingHorizontal: 14, paddingVertical: 12 },
  th: { justifyContent: 'center' },
  thInner: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  thTxt: { fontSize: 11, fontWeight: '800', color: C.muted, letterSpacing: 0.6, textTransform: 'uppercase' as const },
  thArrow: { fontSize: 9, color: C.primary },

  row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#141E18' },
  td: { justifyContent: 'center', paddingRight: 8 },
  cellTxt: { fontSize: 13, color: C.text },
  snTxt: { fontSize: 12, fontWeight: '700', color: C.muted },
  onlineDot: { width: 7, height: 7, borderRadius: 4, backgroundColor: C.success },

  avatar: { width: 38, height: 38, borderRadius: 19, justifyContent: 'center', alignItems: 'center' },
  avatarTxt: { color: '#fff', fontSize: 15, fontWeight: '700' },
  name: { fontSize: 14, fontWeight: '600', color: C.text },
  idTxt: { fontSize: 10, color: C.muted, marginTop: 1 },

  walletBadge: { backgroundColor: C.warningLight, borderRadius: 6, paddingHorizontal: 8, paddingVertical: 3, alignSelf: 'flex-start' },
  walletTxt: { fontSize: 12, color: '#92400e', fontWeight: '700' },

  orderBadge: { minWidth: 30, paddingHorizontal: 9, paddingVertical: 4, borderRadius: 14, backgroundColor: C.primaryLight, alignItems: 'center' },
  orderBadgeZero: { backgroundColor: '#141E18' },
  orderTxt: { fontSize: 13, fontWeight: '800', color: C.primary },
  orderTxtZero: { color: C.muted, fontWeight: '600' },

  statusPill: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 20, alignSelf: 'flex-start' },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  statusTxt: { fontSize: 12, fontWeight: '600' },

  notifyBtn: { backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 8, paddingVertical: 6, paddingHorizontal: 10, justifyContent: 'center' },
  notifyTxt: { fontSize: 14 },
  editBtn: { backgroundColor: C.primaryLight, borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14 },
  editTxt: { color: C.primary, fontWeight: '700', fontSize: 12 },
  delBtn: { backgroundColor: C.dangerLight, borderRadius: 8, paddingVertical: 7, paddingHorizontal: 14 },
  delTxt: { color: C.danger, fontWeight: '700', fontSize: 12 },
});

const ms = StyleSheet.create({
  root: { flex: 1, flexDirection: 'row', justifyContent: 'flex-end' },
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(15,23,42,0.45)' },
  drawer: { height: '100%', backgroundColor: '#0E1512', shadowColor: '#000', shadowOffset: { width: -4, height: 0 }, shadowOpacity: 0.18, shadowRadius: 20, elevation: 16 },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#17201B', paddingHorizontal: 16, paddingTop: 16, paddingBottom: 14, borderBottomWidth: 1, borderBottomColor: C.border, gap: 12 },
  closeBtn: { width: 36, height: 36, borderRadius: 18, backgroundColor: '#141E18', justifyContent: 'center', alignItems: 'center' },
  closeTxt: { fontSize: 14, color: C.muted, fontWeight: '600' },
  title: { flex: 1, fontSize: 18, fontWeight: '700', color: C.text, textAlign: 'center' },
  saveBtn: { backgroundColor: C.primary, paddingHorizontal: 18, paddingVertical: 9, borderRadius: 10, minWidth: 80, alignItems: 'center' },
  saveTxt: { color: '#fff', fontWeight: '700', fontSize: 14 },

  userChip: { flexDirection: 'row', alignItems: 'center', gap: 12, backgroundColor: '#17201B', paddingHorizontal: 16, paddingVertical: 14, borderBottomWidth: 1, borderBottomColor: C.border },
  chipAvatar: { width: 42, height: 42, borderRadius: 21, justifyContent: 'center', alignItems: 'center' },
  chipAvatarTxt: { color: '#fff', fontSize: 17, fontWeight: '700' },
  chipName: { fontSize: 15, fontWeight: '600', color: C.text },
  chipId: { fontSize: 11, color: C.muted, marginTop: 2 },
  chipOrders: { alignItems: 'center', backgroundColor: C.primaryLight, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 6 },
  chipOrdersVal: { fontSize: 16, fontWeight: '800', color: C.primary },
  chipOrdersLbl: { fontSize: 10, color: C.primary, fontWeight: '600' },

  scroll: { padding: 20, paddingBottom: 40 },
  section: { fontSize: 11, fontWeight: '700', color: C.primary, letterSpacing: 1, textTransform: 'uppercase' as const, marginTop: 20, marginBottom: 10, borderBottomWidth: 1, borderBottomColor: 'rgba(41,160,90,0.12)', paddingBottom: 6 },

  errBox: { backgroundColor: C.dangerLight, borderRadius: 10, padding: 12, marginBottom: 4 },
  errTxt: { color: '#991b1b', fontSize: 13, fontWeight: '600' },

  fg: { marginBottom: 14 },
  label: { fontSize: 13, fontWeight: '600', color: '#A8C4AF', marginBottom: 6 },
  input: { borderWidth: 1, borderColor: C.border, borderRadius: 10, paddingHorizontal: 14, paddingVertical: 11, fontSize: 14, color: C.text, backgroundColor: '#fafafa', outlineStyle: 'none' as any },
  hint: { fontSize: 11, color: C.muted, marginTop: 5 },

  switchRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 14, backgroundColor: '#17201B', borderRadius: 12, paddingHorizontal: 14, borderWidth: 1, borderColor: C.border },
  switchLabel: { fontSize: 14, fontWeight: '600', color: C.text },
  switchSub: { fontSize: 12, color: C.muted, marginTop: 2 },
});

const cf = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: 'rgba(15,23,42,0.5)', justifyContent: 'center', alignItems: 'center', padding: 24 },
  card: { backgroundColor: '#17201B', borderRadius: 16, padding: 24, width: '100%', maxWidth: 380, alignItems: 'center', shadowColor: '#000', shadowOffset: { width: 0, height: 8 }, shadowOpacity: 0.2, shadowRadius: 24, elevation: 10 },
  iconWrap: { width: 56, height: 56, borderRadius: 28, backgroundColor: C.dangerLight, justifyContent: 'center', alignItems: 'center', marginBottom: 14 },
  title: { fontSize: 18, fontWeight: '700', color: C.text, marginBottom: 6 },
  body: { fontSize: 13, color: C.muted, textAlign: 'center', lineHeight: 19, marginBottom: 20 },
  actions: { flexDirection: 'row', gap: 10, width: '100%' },
  cancelBtn: { flex: 1, paddingVertical: 11, borderRadius: 10, borderWidth: 1, borderColor: C.border, alignItems: 'center' },
  cancelTxt: { color: C.text, fontWeight: '600', fontSize: 14 },
  confirmBtn: { flex: 1, paddingVertical: 11, borderRadius: 10, backgroundColor: C.danger, alignItems: 'center' },
  confirmTxt: { color: '#fff', fontWeight: '700', fontSize: 14 },
});

const nf = StyleSheet.create({
  header: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 14 },
  title: { fontSize: 17, fontWeight: '700', color: C.text },
  sub: { fontSize: 12, color: C.muted, marginTop: 1 },
  warn: { backgroundColor: C.warningLight, borderRadius: 10, padding: 10, marginBottom: 10 },
  warnTxt: { color: '#92400e', fontSize: 12, fontWeight: '600' },
  preview: { width: '100%', height: 140, borderRadius: 10, marginTop: 8, backgroundColor: '#e2e8f0' },
  sendBtn: { flex: 1, paddingVertical: 11, borderRadius: 10, backgroundColor: C.primary, alignItems: 'center' },
  sendTxt: { color: '#fff', fontWeight: '700', fontSize: 14 },
  progress: { fontSize: 12, color: C.primary, fontWeight: '600', marginTop: 12, textAlign: 'center' },
  labelRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 },
  tokenBtns: { flexDirection: 'row', gap: 6 },
  tokenBtn: { backgroundColor: C.primaryLight, borderRadius: 12, paddingHorizontal: 8, paddingVertical: 3 },
  tokenTxt: { fontSize: 10, fontWeight: '700', color: C.primary },
  hint: { fontSize: 11, color: C.muted, marginTop: 5 },
});
